import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TagModel } from '../../../core/models/api/tag.model';
import { TagService } from '../../../services/tag.service';

type CategoryOption = {
  code: string;
  name: string;
  count: number;
  categoriaTagId: number;
};

@Component({
  selector: 'app-create-tag-dialog',
  templateUrl: './create-tag-dialog.component.html',
  styleUrl: './create-tag-dialog.component.scss'
})
export class CreateTagDialogComponent implements OnChanges {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  /** Lista completa tag (serve per costruire categorie e validare duplicati) */
  @Input() tags: TagModel[] = [];

  /** Categoria da pre-selezionare all'apertura (opzionale) */
  @Input() preselectedCategory: string | null = null;

  @Output() tagCreated = new EventEmitter<TagModel>();

  categories: CategoryOption[] = [];

  newTagCategory: string | null = null;
  newTagCodice = '';
  codiceDuplicato = false;
  savingTag = false;

  constructor(
    private tagService: TagService,
    private messageService: MessageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tags']) {
      this.rebuildCategories();
    }

    if (changes['visible'] && this.visible) {
      this.resetForm();
      this.rebuildCategories();
      if (this.preselectedCategory && this.categories.some(c => c.code === this.preselectedCategory)) {
        this.newTagCategory = this.preselectedCategory;
      }
      this.checkCodiceDuplicato();
    }
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetForm();
    document.body.style.overflow = '';
  }

  private resetForm(): void {
    this.newTagCategory = null;
    this.newTagCodice = '';
    this.codiceDuplicato = false;
    this.savingTag = false;
  }

  private rebuildCategories(): void {
    const map = new Map<string, { name: string; count: number; categoriaTagId: number }>();
    for (const t of this.tags || []) {
      const code = t.categoriaTagCodice;
      const name = t.categoriaTagNome;
      if (!code) continue;
      if (!map.has(code)) {
        map.set(code, { name, count: 1, categoriaTagId: t.categoriaTagId });
      } else {
        const cur = map.get(code)!;
        cur.count += 1;
      }
    }

    this.categories = Array.from(map.entries())
      .map(([code, data]) => ({
        code,
        name: data.name,
        count: data.count,
        categoriaTagId: data.categoriaTagId
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' }));
  }

  onCategoryDropdownToggle(event: any): void {
    const dialogContent = document.querySelector('.create-tag-dialog .p-dialog-content');
    if (event && event.originalEvent && event.originalEvent.type === 'click') {
      if (dialogContent) {
        (dialogContent as HTMLElement).style.overflow = 'visible';
      }
    }
  }

  checkCodiceDuplicato(): void {
    if (!this.newTagCategory || !this.newTagCodice.trim()) {
      this.codiceDuplicato = false;
      return;
    }

    const codiceNormalizzato = this.newTagCodice.trim().toLowerCase();
    const tagsNellaCategoria = (this.tags || []).filter(t => t.categoriaTagCodice === this.newTagCategory);
    this.codiceDuplicato = tagsNellaCategoria.some(t => t.codice.toLowerCase() === codiceNormalizzato);
  }

  isFormValid(): boolean {
    return !!this.newTagCategory && !!this.newTagCodice.trim() && !this.codiceDuplicato;
  }

  private getCategoryId(categoryCode: string): number | null {
    const found = this.categories.find(c => c.code === categoryCode);
    return found ? found.categoriaTagId : null;
  }

  save(): void {
    if (!this.isFormValid()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validazione',
        detail: 'Compila tutti i campi obbligatori e verifica che il codice non sia duplicato',
        life: 4000
      });
      return;
    }

    const categoriaId = this.getCategoryId(this.newTagCategory!);
    if (!categoriaId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Categoria non valida',
        life: 5000
      });
      return;
    }

    this.savingTag = true;
    const tagData = {
      categoriaTagId: categoriaId,
      codice: this.newTagCodice.trim(),
      ordineVisualizzazione: null
    };

    this.tagService.createTag(tagData).subscribe({
      next: (newTag) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Tag creato con successo',
          life: 3000
        });
        this.tagCreated.emit(newTag);
        this.close();
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || 'Errore durante la creazione del tag',
          life: 5000
        });
        this.savingTag = false;
      }
    });
  }

  /**
   * Restituisce l'icona Material Symbols per la categoria
   * (allineata alle 5 nuove categorie principali)
   */
  getCategoryIcon(categoryCode: string): string {
    if (!categoryCode) return 'category';

    const iconMap: Record<string, string> = {
      FASI: 'timeline',
      TEMATICHE: 'schema',
      TIPOLOGIA: 'category',
      PRESTAZIONI: 'speed',
      TIPO_STRUTTURE: 'apartment'
    };

    return iconMap[categoryCode] || 'category';
  }

  getCategoryColor(categoryCode: string): string {
    if (!categoryCode) return '#6b7280';
    const categoryColors: Record<string, string> = {
      FASI: '#0ea5e9',           // azzurro
      TEMATICHE: '#22c55e',      // verde
      TIPOLOGIA: '#8b5cf6',      // viola
      PRESTAZIONI: '#f97316',    // arancione
      TIPO_STRUTTURE: '#ec4899'  // rosa
    };
    return categoryColors[categoryCode] || '#6b7280';
  }

  getCategoryTextColor(categoryCode: string): string {
    const bgColor = this.getCategoryColor(categoryCode);
    const hex = bgColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#1f2937' : '#ffffff';
  }
}


